import { useEffect, useState } from "react";
import { Fingerprint, Trash2, Crown, Plus, LifeBuoy, Mail, Save, ArrowDown, RotateCcw, ShieldAlert, Lock, Calendar, Send, Gift, Users, Copy, Check, Share2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";

function bytesHuman(n) {
  n = Number(n) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} MB`;
  return `${n} B`;
}

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState(false);
  const supported = browserSupportsWebAuthn();

  // admin-only: support config
  const [supportEmail, setSupportEmail] = useState("");
  const [responseText, setResponseText] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);

  // cancellation modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [quota, setQuota] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/billing/portal", { return_url: `${window.location.origin}/settings` });
      if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal. If you paid as a one-off, there's nothing to manage yet — just renew from the Pricing page when your time's up.");
    } finally {
      setPortalBusy(false);
    }
  };

  // weekly digest preferences
  const [digestOptOut, setDigestOptOut] = useState(false);
  const [digestLastSent, setDigestLastSent] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  // gift code redeem
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);

  // referrals
  const [referral, setReferral] = useState(null);
  const [refCopied, setRefCopied] = useState(false);

  const load = async () => {
    const r = await api.get("/webauthn/credentials");
    setCreds(r.data);
    try {
      const q = await api.get("/storage/quota");
      setQuota(q.data);
    } catch { /* ignore */ }
    try {
      const d = await api.get("/me/digest");
      setDigestOptOut(!!d.data.opt_out);
      setDigestLastSent(d.data.last_sent_at || null);
    } catch { /* ignore */ }
    try {
      const ref = await api.get("/me/referral");
      setReferral(ref.data);
    } catch { /* ignore */ }
    if (user?.is_admin) {
      try {
        const s = await api.get("/admin/settings");
        setSupportEmail(s.data.support_email || "");
        setResponseText(s.data.response_time_text || "");
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.is_admin]);

  const addPasskey = async () => {
    setBusy(true);
    try {
      const opts = await api.post("/webauthn/register/options");
      const att = await startRegistration({ optionsJSON: opts.data.options });
      await api.post("/webauthn/register/verify", { challenge_id: opts.data.challenge_id, credential: att, label: navigator.platform || "Device" });
      toast.success("Passkey registered");
      load();
    } catch (e) {
      toast.error(e?.message || "Registration failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/webauthn/credentials/${encodeURIComponent(id)}`);
    toast.success("Passkey removed");
    load();
  };

  const saveSupport = async () => {
    if (!supportEmail.includes("@")) { toast.error("Valid email required"); return; }
    setSavingCfg(true);
    try {
      await api.put("/admin/settings", { support_email: supportEmail, response_time_text: responseText });
      toast.success("Support settings updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSavingCfg(false); }
  };

  const toggleDigest = async (nextOptOut) => {
    setDigestBusy(true);
    try {
      await api.put("/me/digest", { opt_out: nextOptOut });
      setDigestOptOut(nextOptOut);
      toast.success(nextOptOut ? "You won't receive the weekly digest" : "Weekly digest is on");
    } catch {
      toast.error("Could not update preference");
    } finally { setDigestBusy(false); }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const r = await api.post("/me/digest/send-now");
      if (r.data.skipped) {
        toast(r.data.reason || "Add some words first", { duration: 6000 });
      } else {
        toast.success("Digest sent — check your inbox");
        setDigestLastSent(new Date().toISOString());
      }
    } catch {
      toast.error("Could not send digest");
    } finally { setDigestBusy(false); }
  };

  const submitRedeem = async (e) => {
    if (e) e.preventDefault();
    const code = redeemCode.trim();
    if (!code) { toast.error("Enter a code"); return; }
    setRedeemBusy(true);
    try {
      const r = await api.post("/codes/redeem", { code });
      toast.success(r.data.message || "Redeemed!", { duration: 6000 });
      setRedeemCode("");
      await checkAuth(); // refresh plan in user state
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem");
    } finally { setRedeemBusy(false); }
  };

  const copyReferralLink = async () => {
    if (!referral?.url) return;
    try {
      await navigator.clipboard.writeText(referral.url);
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 1500);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareReferralLink = async () => {
    if (!referral?.url) return;
    const text = "I'm using Studict to ace revision — AI scans my textbooks into a personal glossary. Try it free:";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Studict — Smart Student Dictionary", text, url: referral.url });
      } else {
        await copyReferralLink();
      }
    } catch (_) { /* user cancelled */ }
  };

  const reactivate = async () => {
    setCancelBusy(true);
    try {
      const r = await api.post("/billing/reactivate");
      toast.success(r.data.message || "Plan reactivated");
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reactivate");
    } finally { setCancelBusy(false); }
  };

  const confirmCancel = async () => {
    setCancelBusy(true);
    try {
      const r = await api.post("/billing/cancel");
      toast.success(r.data.is_cancelling ? "Cancellation scheduled" : "Downgraded");
      setShowCancelModal(false);
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setCancelBusy(false); }
  };

  // Compute what would be locked if dropped to Free right now
  const overQuotaBytes = quota ? Math.max(0, (quota.used_bytes || 0) - (100 * 1024 * 1024)) : 0;
  const wouldLockSomething = overQuotaBytes > 0;
  const proUntilDate = user?.pro_until ? new Date(user.pro_until) : null;
  const daysLeft = proUntilDate ? Math.max(0, Math.ceil((proUntilDate - new Date()) / 86400000)) : 0;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Settings</p>
        <h1 className="font-display text-4xl font-black mt-1">Your account</h1>
      </header>

      <section className="nb-card p-6" data-testid="plan-section">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Crown className="w-5 h-5" strokeWidth={2.5}/> Plan</h2>
        <p className="mt-2 text-sm font-medium text-slate-700">
          You're on <span className="nb-tag bg-yellow-300">{user?.plan === "pro_plus" ? "Pro Plus" : user?.plan === "pro_standard" ? "Pro" : "Free"}</span>
          {user?.pro_until && <> · until {new Date(user.pro_until).toLocaleDateString()}</>}
        </p>

        {user?.is_cancelling && (
          <div data-testid="cancellation-scheduled-banner" className="mt-4 p-4 border-[3px] border-black rounded-lg bg-amber-100 shadow-[4px_4px_0_#111]">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5}/>
              <div className="text-sm">
                <p className="font-bold">Cancellation scheduled.</p>
                <p className="mt-1 text-slate-700">
                  You keep <strong>{user?.plan === "pro_plus" ? "Pro Plus" : "Pro"}</strong> until <strong>{proUntilDate?.toLocaleDateString()}</strong> ({daysLeft} day{daysLeft !== 1 ? "s" : ""} left), then your account moves to Free. Your data is never deleted — over-quota files just become read-only.
                </p>
                <button
                  data-testid="reactivate-plan-btn"
                  onClick={reactivate}
                  disabled={cancelBusy}
                  className="nb-btn mt-3 text-sm inline-flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" strokeWidth={2.5}/> Reactivate plan
                </button>
              </div>
            </div>
          </div>
        )}

        {user?.plan && user.plan !== "free" && (
          <div className="mt-5 flex flex-wrap gap-3">
            <a href="/pricing" className="nb-btn-secondary text-sm inline-flex items-center gap-2">Manage / renew</a>
            <button
              data-testid="open-portal-btn"
              onClick={openBillingPortal}
              disabled={portalBusy}
              className="nb-btn-secondary text-sm inline-flex items-center gap-2"
              title="Cancel auto-renew or update your card"
            >
              <Crown className="w-4 h-4" strokeWidth={2.5}/> {portalBusy ? "Opening…" : "Manage subscription"}
            </button>
            {!user?.is_cancelling && (
              <button
                data-testid="cancel-plan-btn"
                onClick={() => setShowCancelModal(true)}
                className="nb-btn-danger text-sm inline-flex items-center gap-2"
              >
                <ArrowDown className="w-4 h-4" strokeWidth={2.5}/> Cancel plan
              </button>
            )}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-3">
          If you opted into auto-renew at checkout, the <strong>Manage subscription</strong> button opens a secure Stripe page where you can cancel anytime or update your card. Otherwise plans run as 30-day one-off purchases.
        </p>
      </section>

      {showCancelModal && (
        <div data-testid="cancel-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !cancelBusy && setShowCancelModal(false)}>
          <div className="nb-card max-w-lg w-full bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-black flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-500" strokeWidth={2.5}/> Before you cancel
            </h3>
            <div className="mt-4 space-y-3 text-sm">
              <div className="p-3 border-2 border-black rounded-lg bg-amber-50 flex items-start gap-2">
                <Calendar className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5}/>
                <span>You'll <strong>keep {user?.plan === "pro_plus" ? "Pro Plus" : "Pro"} until {proUntilDate?.toLocaleDateString()}</strong> ({daysLeft} day{daysLeft !== 1 ? "s" : ""}) — no refund needed because nothing is auto-billed.</span>
              </div>
              {wouldLockSomething ? (
                <div className="p-3 border-2 border-black rounded-lg bg-rose-50 flex items-start gap-2">
                  <Lock className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5}/>
                  <div>
                    <p>You currently use <strong>{bytesHuman(quota?.used_bytes)}</strong> of storage. After your plan ends:</p>
                    <ul className="mt-2 list-disc pl-5 space-y-1">
                      <li><strong>{bytesHuman(overQuotaBytes)}</strong> beyond the 100 MB Free quota will become <strong>read-only</strong></li>
                      <li>Files are <strong>never deleted</strong> — they just stay locked until you resume Pro</li>
                      <li>Words, categories, quizzes and tests are unaffected</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="p-3 border-2 border-black rounded-lg bg-emerald-50 text-sm">
                  You're well within the Free 100 MB quota — nothing will be locked.
                </div>
              )}
              <div className="p-3 border-2 border-black rounded-lg bg-slate-50 text-sm">
                Changed your mind during the grace period? Hit <strong>Reactivate</strong> in Settings to undo with no charge.
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3 justify-end">
              <button
                data-testid="cancel-modal-back"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelBusy}
                className="nb-btn-secondary text-sm"
              >Keep my plan</button>
              <button
                data-testid="cancel-modal-confirm"
                onClick={confirmCancel}
                disabled={cancelBusy}
                className="nb-btn-danger text-sm inline-flex items-center gap-2"
              >
                {cancelBusy ? "Cancelling…" : <><ArrowDown className="w-4 h-4" strokeWidth={2.5}/> Schedule cancellation</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="nb-card p-6" data-testid="redeem-section">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Gift className="w-5 h-5" strokeWidth={2.5}/> Have a code?</h2>
        <p className="text-sm text-slate-600 mt-1">Got a promo or gift code? Enter it below to unlock Pro or Pro Plus.</p>
        <form onSubmit={submitRedeem} className="mt-4 flex flex-wrap gap-2">
          <input
            data-testid="redeem-input"
            type="text"
            value={redeemCode}
            onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX-XXXX"
            className="nb-input flex-1 min-w-[200px] font-mono tracking-widest"
            disabled={redeemBusy}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            disabled={redeemBusy || !redeemCode.trim()}
            data-testid="redeem-submit-btn"
            className="nb-btn-primary inline-flex items-center gap-2"
          >
            <Gift className="w-4 h-4" strokeWidth={2.5}/> {redeemBusy ? "Redeeming…" : "Redeem"}
          </button>
        </form>
      </section>

      <section className="nb-card p-6" data-testid="referral-section" style={{ background: "#86EFAC33" }}>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Users className="w-5 h-5" strokeWidth={2.5}/> Refer a friend, get 7 free days</h2>
        <p className="text-sm text-slate-700 mt-1">Share your link. When a friend signs up <strong>and upgrades to Pro</strong>, you both get <strong>7 days of Pro Plus</strong> automatically.</p>
        {referral ? (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <code
                data-testid="referral-link"
                className="flex-1 min-w-[200px] bg-white border-2 border-slate-900 rounded-md px-3 py-2 text-sm font-mono tracking-tight overflow-x-auto"
                style={{ boxShadow: "2px 2px 0 0 #0F172A" }}
              >{referral.url}</code>
              <button
                onClick={copyReferralLink}
                data-testid="referral-copy-btn"
                className="nb-btn-secondary text-sm inline-flex items-center gap-2"
              >
                {refCopied ? <><Check className="w-4 h-4" strokeWidth={2.5}/> Copied</> : <><Copy className="w-4 h-4" strokeWidth={2.5}/> Copy</>}
              </button>
              <button
                onClick={shareReferralLink}
                data-testid="referral-share-btn"
                className="nb-btn-primary text-sm inline-flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" strokeWidth={2.5}/> Share
              </button>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="nb-card-sm p-3 bg-white">
                <div className="text-xs font-bold uppercase tracking-widest">Friends joined</div>
                <div className="font-display text-2xl font-black mt-1">{referral.referred_count}</div>
              </div>
              <div className="nb-card-sm p-3 bg-white">
                <div className="text-xs font-bold uppercase tracking-widest">Converted</div>
                <div className="font-display text-2xl font-black mt-1">{referral.converted_count}</div>
              </div>
              <div className="nb-card-sm p-3 bg-yellow-300">
                <div className="text-xs font-bold uppercase tracking-widest">Days earned</div>
                <div className="font-display text-2xl font-black mt-1">{referral.bonus_days_earned}</div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-600 mt-3">Loading your link…</p>
        )}
      </section>

      <section className="nb-card p-6" data-testid="digest-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Send className="w-5 h-5" strokeWidth={2.5}/> Weekly study digest</h2>
            <p className="text-sm text-slate-600 mt-1">A 30-second Sunday email: words added this week, your weakest term, and a 5-question quiz.</p>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <span className="text-xs font-bold uppercase tracking-widest">{digestOptOut ? "Off" : "On"}</span>
            <span className={`relative inline-block w-12 h-7 border-2 border-slate-900 rounded-full ${digestOptOut ? "bg-slate-200" : "bg-emerald-300"}`} style={{ boxShadow: "2px 2px 0 0 #0F172A" }}>
              <span className={`absolute top-0.5 ${digestOptOut ? "left-0.5" : "right-0.5"} w-5 h-5 bg-white border-2 border-slate-900 rounded-full transition-all`}/>
            </span>
            <input
              data-testid="digest-toggle"
              type="checkbox"
              className="sr-only"
              checked={!digestOptOut}
              onChange={(e) => toggleDigest(!e.target.checked)}
              disabled={digestBusy}
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-500">
          {digestLastSent && <span>Last sent {new Date(digestLastSent).toLocaleDateString()}</span>}
          <button
            onClick={sendDigestNow}
            disabled={digestBusy || digestOptOut}
            data-testid="digest-send-now-btn"
            className="nb-btn-secondary text-xs inline-flex items-center gap-2"
            title={digestOptOut ? "Turn the digest on first" : "Preview your digest in your inbox"}
          >
            <Send className="w-3 h-3" strokeWidth={2.5}/> Send me a preview
          </button>
        </div>
      </section>

      <section className="nb-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Fingerprint className="w-5 h-5" strokeWidth={2.5}/> Passkeys</h2>
            <p className="text-sm text-slate-600 mt-1">Sign in with Face ID, Touch ID or Windows Hello on this device.</p>
          </div>
          <button onClick={addPasskey} disabled={busy || !supported} data-testid="add-passkey-btn" className="nb-btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" strokeWidth={2.5}/> {busy ? "…" : "Add passkey"}
          </button>
        </div>
        {!supported && <p className="text-xs text-slate-500 mt-3">This browser doesn't support passkeys.</p>}

        <div className="mt-5 space-y-2">
          {creds.length === 0 ? (
            <p className="text-sm text-slate-500">No passkeys yet — add one to enable biometric sign-in.</p>
          ) : creds.map((c) => (
            <div key={c.credential_id} className="nb-card-sm p-3 flex items-center justify-between" data-testid={`passkey-${c.label}`}>
              <div>
                <div className="font-bold">{c.label}</div>
                <div className="text-xs text-slate-500">Added {new Date(c.created_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => remove(c.credential_id)} className="nb-btn-danger text-xs inline-flex items-center gap-1 py-2 px-3"><Trash2 className="w-3 h-3" strokeWidth={2.5}/></button>
            </div>
          ))}
        </div>
      </section>

      {user?.is_admin && (
        <section className="nb-card p-6" style={{ background: "#F9A8D433" }}>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><LifeBuoy className="w-5 h-5" strokeWidth={2.5}/> Support settings <span className="nb-tag bg-pink-300 text-xs">Admin</span></h2>
          <p className="text-sm text-slate-700 mt-1">Where support tickets are routed (not shown to users) and the SLA you advertise.</p>

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs uppercase font-bold tracking-widest flex items-center gap-1"><Mail className="w-3 h-3" strokeWidth={2.5}/> Support email recipient</label>
              <input data-testid="support-email-input" type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="nb-input mt-1" placeholder="you@example.com"/>
              <p className="text-xs text-slate-500 mt-1">Tickets are stored in the admin inbox now. When you connect Resend later, this is where emails will be forwarded.</p>
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">Response-time message shown to users</label>
              <input data-testid="response-text-input" value={responseText} onChange={(e) => setResponseText(e.target.value)} className="nb-input mt-1" placeholder="We'll respond within 3 business days."/>
            </div>
            <button onClick={saveSupport} disabled={savingCfg} data-testid="save-support-btn" className="nb-btn-primary inline-flex items-center gap-2">
              <Save className="w-4 h-4" strokeWidth={2.5}/> {savingCfg ? "Saving…" : "Save"}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
